<template>
  <div class="lucky-wheel-container">
    <div class="text-center mb-4">
      <h2 class="text-2xl font-bold text-white mb-1">Glücksrad</h2>
      <p class="text-white/70 text-sm">Drehe das Rad und gewinne tolle Angebote!</p>
      <div class="flex items-center justify-center gap-2 mt-2">
        <div v-for="i in maxSpins" :key="i" :class="['w-3 h-3 rounded-full transition-all', i <= spinsLeft ? 'bg-yellow-400 shadow-lg shadow-yellow-400/40' : 'bg-white/20']"></div>
        <span class="text-xs text-white/50 ml-1">{{ spinsLeft }}/{{ maxSpins }} Versuche</span>
      </div>
    </div>

    <div class="wheel-wrapper" ref="wheelWrapper">
      <div class="wheel-pointer">
        <svg width="30" height="40" viewBox="0 0 30 40">
          <polygon points="15,0 30,40 0,40" fill="#FFD700" stroke="#fff" stroke-width="2"/>
        </svg>
      </div>
      <div
        class="wheel"
        :style="{ transform: `rotate(${rotation}deg)`, transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none' }"
      >
        <svg viewBox="0 0 300 300" class="w-full h-full">
          <g v-for="(seg, i) in segments" :key="i">
            <path
              :d="describeArc(150, 150, 140, i * segmentAngle, (i + 1) * segmentAngle)"
              :fill="seg.color || defaultColors[i % defaultColors.length]"
              stroke="#1a1a2e" stroke-width="2"
            />
            <text :transform="getTextTransform(i)" fill="white" font-size="10" font-weight="bold" text-anchor="middle" dominant-baseline="middle">
              {{ seg.emoji || '' }} {{ truncateLabel(seg.label) }}
            </text>
          </g>
          <circle cx="150" cy="150" r="28" fill="#1a1a2e" stroke="#FFD700" stroke-width="3"/>
          <text x="150" y="150" fill="#FFD700" font-size="20" text-anchor="middle" dominant-baseline="middle">🎰</text>
        </svg>
      </div>
    </div>

    <div class="mt-6 text-center">
      <!-- Spin button -->
      <button
        v-if="!spinning && !result && spinsLeft > 0"
        @click="spin"
        class="spin-btn"
        :disabled="loading"
      >
        <span v-if="loading" class="animate-spin inline-block mr-2">⏳</span>
        <span v-else class="mr-2 text-xl">🎯</span>
        Drehen!
      </button>

      <div v-if="spinsLeft <= 0 && !result && !spinning" class="text-white/50 text-sm py-4">
        Keine Versuche mehr übrig. Nächstes Mal hast du wieder Glück!
      </div>

      <!-- Result panel -->
      <div v-if="result && !spinning" class="result-panel animate-bounce-in">
        <!-- WIN -->
        <div v-if="result.offer" class="win-state">
          <div class="text-5xl mb-3">🎉</div>
          <h3 class="text-xl font-bold text-yellow-400 mb-1">Gewonnen!</h3>
          <p class="text-white/60 text-xs mb-1">{{ winSegmentLabel }}</p>
          <p class="text-white mb-4">{{ result.offer.message }}</p>

          <!-- Product selection (drinks/products to choose from) -->
          <div v-if="showProductPicker" class="product-picker">
            <p class="text-sm font-bold text-white/80 mb-2">Wähle dein Produkt:</p>
            <div class="picker-grid">
              <button
                v-for="p in selectableProducts" :key="p.id"
                @click="selectProduct(p)"
                :class="['picker-item', selectedProduct?.id === p.id ? 'picker-selected' : '']"
              >
                <img v-if="p.image_url" :src="p.image_url" :alt="p.name" class="picker-img" @error="$event.target.style.display='none'" />
                <div v-else class="picker-img-placeholder">{{ p.is_beverage ? '🥤' : '🍽️' }}</div>
                <span class="picker-name">{{ p.name }}</span>
                <span class="picker-price">€{{ parseFloat(p.price).toFixed(2) }}</span>
              </button>
            </div>
          </div>

          <!-- Confirm: add free item to cart -->
          <button
            v-if="result.offer.type === 'freeItem' && canConfirm && !addedToCart"
            @click="addFreeItemToCart"
            class="add-to-cart-btn mt-3"
          >
            {{ confirmProductName }} GRATIS in den Warenkorb 🛒
          </button>

          <!-- Confirm: apply discount to selected product -->
          <button
            v-if="result.offer.type === 'discount' && canConfirm && !addedToCart"
            @click="applyDiscountToProduct"
            class="add-to-cart-btn mt-3"
          >
            {{ confirmProductName }} mit {{ result.offer.value }}% Rabatt 🏷️
          </button>

          <!-- Generic discount (no product picker) -->
          <button
            v-if="result.offer.type === 'discount' && !showProductPicker && !addedToCart"
            @click="applyDiscountReward"
            class="add-to-cart-btn mt-3"
          >
            {{ result.offer.value }}% Rabatt einlösen 🏷️
          </button>

          <div v-if="addedToCart" class="added-feedback mt-3">
            <span class="text-green-400 font-bold">✓ Zum Warenkorb hinzugefügt!</span>
          </div>
        </div>

        <!-- LOSE -->
        <div v-else class="lose-state">
          <div class="text-5xl mb-3">😅</div>
          <h3 class="text-xl font-bold text-white/80 mb-2">Vielleicht beim nächsten Dreh!</h3>
        </div>

        <button v-if="spinsLeft > 0" @click="resetWheel" class="retry-btn mt-4">
          Nochmal drehen ({{ spinsLeft }} übrig) 🔄
        </button>
        <button v-else @click="$emit('close')" class="retry-btn mt-4">
          Schließen
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useGamificationStore } from '@/stores/gamification'

const emit = defineEmits(['add-to-cart', 'apply-discount', 'close'])
const gamificationStore = useGamificationStore()

const maxSpins = 3
const spinsLeft = ref(maxSpins)
const spinning = ref(false)
const loading = ref(false)
const rotation = ref(0)
const result = ref(null)
const segments = ref([])
const addedToCart = ref(false)
const selectedProduct = ref(null)

const defaultColors = ['#FF6B35', '#00B4D8', '#7B2FBE', '#2EC4B6', '#E71D36', '#FFD700', '#4A4A4A', '#FF1493']
const segmentAngle = computed(() => segments.value.length > 0 ? 360 / segments.value.length : 45)

const winSegmentLabel = computed(() => {
  if (!result.value || result.value.winIndex == null || !segments.value.length) return ''
  const seg = segments.value[result.value.winIndex]
  return seg ? `${seg.emoji || ''} ${seg.label}`.trim() : ''
})

const selectableProducts = computed(() => result.value?.offer?.selectableProducts || [])
const showProductPicker = computed(() => selectableProducts.value.length > 0 && !addedToCart.value)

const canConfirm = computed(() => {
  if (!showProductPicker.value) return !addedToCart.value
  return selectedProduct.value != null
})

const confirmProductName = computed(() => {
  if (selectedProduct.value) return selectedProduct.value.name
  if (result.value?.offer?.product) return result.value.offer.product.name
  return 'Produkt'
})

function selectProduct(p) {
  selectedProduct.value = p
}

async function spin() {
  if (spinsLeft.value <= 0) return
  loading.value = true
  addedToCart.value = false
  selectedProduct.value = null

  try {
    const sessionId = parseInt(localStorage.getItem('sessionNumericId')) || null
    const identifier = localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''

    const data = await gamificationStore.spinLuckyWheel(sessionId, identifier)
    if (!data || !data.segments || data.segments.length === 0) {
      segments.value = [
        { label: 'Nochmal!', type: 'retry', emoji: '🔄', color: '#4A4A4A' },
        { label: '5% Rabatt', type: 'discount', emoji: '🏷️', color: '#FF6B35' },
        { label: '10% Rabatt', type: 'discount', emoji: '🎉', color: '#00B4D8' },
        { label: 'Nochmal!', type: 'retry', emoji: '🔄', color: '#7B2FBE' },
        { label: 'Gratis-Getränk', type: 'freeItem', emoji: '🥤', color: '#2EC4B6' },
        { label: '15% Rabatt', type: 'discount', emoji: '🔥', color: '#E71D36' },
      ]
    } else {
      segments.value = data.segments
    }

    await new Promise(r => setTimeout(r, 100))

    const winIdx = data?.winIndex ?? 0
    const segAngle = segmentAngle.value
    const targetAngle = 360 - (winIdx * segAngle + segAngle / 2)
    const currentAngle = rotation.value % 360
    const needed = ((targetAngle - currentAngle) % 360 + 360) % 360
    const fullSpins = 5 + Math.floor(Math.random() * 3)
    rotation.value = rotation.value + fullSpins * 360 + needed

    spinning.value = true
    result.value = data
    spinsLeft.value--

    setTimeout(() => {
      spinning.value = false
      if (navigator.vibrate) navigator.vibrate([200, 100, 200])

      const winSeg = data.segments?.[winIdx]
      if (winSeg?.type === 'retry') {
        setTimeout(() => { result.value = null }, 1500)
      }
    }, 4200)
  } catch (e) {
    console.error('Lucky wheel spin failed:', e)
    spinning.value = false
  } finally {
    loading.value = false
  }
}

function addFreeItemToCart() {
  if (addedToCart.value) return
  const product = selectedProduct.value || result.value?.offer?.product
  if (!product) return

  addedToCart.value = true
  emit('add-to-cart', {
    id: product.id,
    name: product.name,
    price: 0,
    originalPrice: product.price,
    imageUrl: product.image_url,
    isFreeReward: true,
    rewardSource: 'Glücksrad',
  })
}

function applyDiscountToProduct() {
  if (addedToCart.value || !selectedProduct.value) return
  addedToCart.value = true
  const value = result.value?.offer?.value || 10
  const p = selectedProduct.value
  const discountedPrice = Math.max(0, parseFloat(p.price) * (1 - value / 100))

  emit('add-to-cart', {
    id: p.id,
    name: `${p.name} (${value}% Rabatt)`,
    price: Math.round(discountedPrice * 100) / 100,
    originalPrice: p.price,
    imageUrl: p.image_url,
    rewardSource: 'Glücksrad',
  })
}

function applyDiscountReward() {
  if (addedToCart.value) return
  addedToCart.value = true
  const value = result.value?.offer?.value
  emit('apply-discount', {
    type: 'discount',
    value,
    message: result.value?.offer?.message || `${value}% Rabatt auf deine nächste Bestellung!`,
    gameName: 'Glücksrad',
  })
}

function resetWheel() {
  result.value = null
  spinning.value = false
  addedToCart.value = false
  selectedProduct.value = null
}

function truncateLabel(label) {
  return label?.length > 14 ? label.slice(0, 12) + '…' : label
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle)
  const end = polarToCartesian(cx, cy, r, startAngle)
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1'
  return `M${cx},${cy} L${start.x},${start.y} A${r},${r} 0 ${largeArc},0 ${end.x},${end.y} Z`
}

function getTextTransform(i) {
  const midAngle = (i + 0.5) * segmentAngle.value
  const rad = ((midAngle - 90) * Math.PI) / 180
  const tx = 150 + 90 * Math.cos(rad)
  const ty = 150 + 90 * Math.sin(rad)
  return `translate(${tx}, ${ty}) rotate(${midAngle})`
}
</script>

<style scoped>
.lucky-wheel-container { @apply flex flex-col items-center py-4 px-4; }
.wheel-wrapper { position: relative; width: 260px; height: 260px; }
.wheel { width: 100%; height: 100%; filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.3)); }
.wheel-pointer { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); z-index: 10; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5)); }

.spin-btn {
  @apply px-8 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold text-lg rounded-full shadow-lg;
  @apply hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 transform hover:scale-105 active:scale-95;
}
.spin-btn:disabled { @apply opacity-50 cursor-not-allowed; }

.add-to-cart-btn {
  @apply px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-full;
  @apply hover:from-green-400 hover:to-emerald-400 transition-all duration-300 transform hover:scale-105;
}
.retry-btn {
  @apply px-6 py-2 bg-white/10 text-white/70 rounded-full text-sm hover:bg-white/20 transition-all duration-300;
}
.added-feedback {
  @apply py-3 px-6 rounded-full bg-green-500/10 border border-green-500/30;
  animation: feedbackPop 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
.result-panel { @apply p-5 rounded-2xl bg-white/10 backdrop-blur-sm; }
.animate-bounce-in { animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55); }

/* Product picker */
.product-picker { @apply max-h-48 overflow-y-auto rounded-xl bg-black/20 p-2 mb-2; }
.picker-grid { @apply grid grid-cols-2 gap-2; }
.picker-item {
  @apply flex flex-col items-center p-2 rounded-xl bg-white/5 border border-white/10 transition-all cursor-pointer;
  @apply hover:bg-white/10 hover:border-white/20;
}
.picker-selected {
  @apply bg-purple-500/20 border-purple-400/60 ring-2 ring-purple-400/40;
}
.picker-img { @apply w-12 h-12 rounded-lg object-cover mb-1; }
.picker-img-placeholder { @apply w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-xl mb-1; }
.picker-name { @apply text-[11px] text-white font-semibold text-center leading-tight line-clamp-2; }
.picker-price { @apply text-[10px] text-yellow-400 font-bold; }

@keyframes feedbackPop { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
@keyframes bounceIn { 0% { transform: scale(0); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
</style>
